import type {
  BalanceSnapshot,
  Brief,
  Card,
  FlightLive,
  Item,
  ItemDetails,
  Perk,
  Place,
  Program,
  Redemption,
  Trip,
} from '../schema';
import { instantFromLocal } from '../time';
import { MPLS_ITEMS, MPLS_PLACES, MPLS_TRIP } from './minneapolis';

/**
 * Sample data for screenshots, design review and the "Load sample trips" switch.
 * Everything here is made up except the Minneapolis shape, which mirrors the test fixture.
 * Every record carries sourceId 'src_sample' so it can be removed in one go.
 */

export const SAMPLE_SOURCE = 'src_sample';
const T0 = Date.UTC(2025, 0, 1);

const base = (id: string) => ({ id, rev: 0, updatedAt: T0, createdAt: T0, deleted: false });

interface AirportSeed {
  iata: string;
  name: string;
  city: string;
  region: string;
  regionCode: string | null;
  country: string;
  countryCode: string;
  lat: number;
  lon: number;
  tz: string;
}

const AIRPORTS: AirportSeed[] = [
  {
    iata: 'LGA',
    name: 'LaGuardia Airport',
    city: 'New York',
    region: 'New York',
    regionCode: 'NY',
    country: 'United States',
    countryCode: 'US',
    lat: 40.7772,
    lon: -73.8726,
    tz: 'America/New_York',
  },
  {
    iata: 'JFK',
    name: 'John F Kennedy International Airport',
    city: 'New York',
    region: 'New York',
    regionCode: 'NY',
    country: 'United States',
    countryCode: 'US',
    lat: 40.6398,
    lon: -73.7789,
    tz: 'America/New_York',
  },
  {
    iata: 'LIS',
    name: 'Humberto Delgado Airport',
    city: 'Lisbon',
    region: 'Lisbon',
    regionCode: null,
    country: 'Portugal',
    countryCode: 'PT',
    lat: 38.7813,
    lon: -9.1359,
    tz: 'Europe/Lisbon',
  },
  {
    iata: 'ORD',
    name: "Chicago O'Hare International Airport",
    city: 'Chicago',
    region: 'Illinois',
    regionCode: 'IL',
    country: 'United States',
    countryCode: 'US',
    lat: 41.9786,
    lon: -87.9048,
    tz: 'America/Chicago',
  },
  {
    iata: 'DEN',
    name: 'Denver International Airport',
    city: 'Denver',
    region: 'Colorado',
    regionCode: 'CO',
    country: 'United States',
    countryCode: 'US',
    lat: 39.8617,
    lon: -104.6732,
    tz: 'America/Denver',
  },
  {
    iata: 'BOS',
    name: 'Logan International Airport',
    city: 'Boston',
    region: 'Massachusetts',
    regionCode: 'MA',
    country: 'United States',
    countryCode: 'US',
    lat: 42.3643,
    lon: -71.0052,
    tz: 'America/New_York',
  },
  {
    iata: 'SEA',
    name: 'Seattle-Tacoma International Airport',
    city: 'Seattle',
    region: 'Washington',
    regionCode: 'WA',
    country: 'United States',
    countryCode: 'US',
    lat: 47.449,
    lon: -122.3093,
    tz: 'America/Los_Angeles',
  },
  {
    iata: 'CDG',
    name: 'Charles de Gaulle International Airport',
    city: 'Paris',
    region: 'Ile-de-France',
    regionCode: null,
    country: 'France',
    countryCode: 'FR',
    lat: 49.0128,
    lon: 2.55,
    tz: 'Europe/Paris',
  },
  {
    iata: 'AUS',
    name: 'Austin-Bergstrom International Airport',
    city: 'Austin',
    region: 'Texas',
    regionCode: 'TX',
    country: 'United States',
    countryCode: 'US',
    lat: 30.1945,
    lon: -97.6699,
    tz: 'America/Chicago',
  },
  {
    iata: 'ATL',
    name: 'Hartsfield-Jackson Atlanta International Airport',
    city: 'Atlanta',
    region: 'Georgia',
    regionCode: 'GA',
    country: 'United States',
    countryCode: 'US',
    lat: 33.6367,
    lon: -84.4281,
    tz: 'America/New_York',
  },
];

const airportPlace = (a: AirportSeed): Place => ({
  ...base(`plc_air_${a.iata}`),
  name: a.name,
  address: null,
  lat: a.lat,
  lon: a.lon,
  tz: a.tz,
  city: a.city,
  region: a.region,
  regionCode: a.regionCode,
  country: a.country,
  countryCode: a.countryCode,
  iata: a.iata,
  kind: 'airport',
  geo: 'dataset',
});

const airportById = new Map<string, Place>([
  ...AIRPORTS.map((a) => [a.iata, airportPlace(a)] as const),
  ['RSW', { ...MPLS_PLACES.find((p) => p.iata === 'RSW')!, id: 'plc_air_RSW' }],
  ['MSP', { ...MPLS_PLACES.find((p) => p.iata === 'MSP')!, id: 'plc_air_MSP' }],
]);
const ap = (iata: string) => airportById.get(iata)!;

interface StaySeed {
  key: string;
  name: string;
  kind: 'hotel' | 'airbnb';
  brand?: string;
  program?: string;
  city: string;
  region: string;
  regionCode: string | null;
  country: string;
  countryCode: string;
  lat: number;
  lon: number;
  tz: string;
  address: string;
}

const places: Place[] = [];
const items: Item[] = [];
const trips: Trip[] = [];

function stayPlace(s: StaySeed): Place {
  const p: Place = {
    ...base(`plc_${s.key}`),
    name: s.name,
    address: s.address,
    lat: s.lat,
    lon: s.lon,
    tz: s.tz,
    city: s.city,
    region: s.region,
    regionCode: s.regionCode,
    country: s.country,
    countryCode: s.countryCode,
    iata: null,
    kind: 'lodging',
    geo: 'geocoder',
  };
  places.push(p);
  return p;
}

function addItem(
  tripId: string,
  id: string,
  p: Omit<Partial<Item>, 'details'> & Pick<Item, 'type' | 'title'> & { details: ItemDetails },
) {
  items.push({
    ...base(id),
    tripId,
    tripLocked: false,
    status: 'active',
    startAt: null,
    startTz: null,
    endAt: null,
    endTz: null,
    allDay: false,
    startPlaceId: null,
    endPlaceId: null,
    provider: null,
    confirmation: null,
    phone: null,
    url: null,
    notes: '',
    sourceId: SAMPLE_SOURCE,
    cost: null,
    ...p,
  });
}

const AIRLINES: Record<string, string> = {
  DL: 'Delta',
  AA: 'American',
  UA: 'United',
  B6: 'JetBlue',
  TP: 'TAP Air Portugal',
  AF: 'Air France',
  AS: 'Alaska',
};

function flight(
  tripId: string,
  n: string,
  from: string,
  to: string,
  dep: string,
  arr: string,
  extra: Partial<ItemDetails & { seat: string }> = {},
  confirmation = 'K7Q2ZP',
) {
  const a = ap(from);
  const b = ap(to);
  const code = n.slice(0, 2);
  addItem(tripId, `itm_${tripId}_${n.replace(/\s/g, '')}`, {
    type: 'flight',
    title: `${AIRLINES[code] ?? code} ${n.slice(2).trim()}`,
    provider: AIRLINES[code] ?? code,
    confirmation,
    startAt: instantFromLocal(dep, a.tz!),
    startTz: a.tz,
    endAt: instantFromLocal(arr, b.tz!),
    endTz: b.tz,
    startPlaceId: a.id,
    endPlaceId: b.id,
    details: {
      type: 'flight',
      airline: code,
      flightNumber: n.slice(2).trim(),
      origin: from,
      destination: to,
      cabin: 'Main',
      ...extra,
    } as ItemDetails,
  });
}

function stay(
  tripId: string,
  s: StaySeed,
  checkIn: string,
  checkOut: string,
  confirmation: string,
) {
  const p = stayPlace(s);
  addItem(tripId, `itm_${tripId}_${s.key}`, {
    type: 'lodging',
    title: s.name,
    provider: s.brand ?? (s.kind === 'airbnb' ? 'Airbnb' : null),
    confirmation,
    startAt: instantFromLocal(checkIn, s.tz),
    startTz: s.tz,
    endAt: instantFromLocal(checkOut, s.tz),
    endTz: s.tz,
    startPlaceId: p.id,
    details: {
      type: 'lodging',
      kind: s.kind,
      propertyName: s.name,
      brand: s.brand,
      program: s.program,
    },
  });
}

function trip(id: string): string {
  trips.push({
    ...base(id),
    title: null,
    startDate: null,
    endDate: null,
    coverAttachmentId: null,
    notes: '',
    pinned: false,
  });
  return id;
}

// ---------- Upcoming ----------

{
  const t = trip('trp_nyc26');
  flight(t, 'B6 1032', 'RSW', 'LGA', '2026-11-19T08:15', '2026-11-19T11:02', { seat: '2C' });
  stay(
    t,
    {
      key: 'nyc_hotel',
      name: 'The Wythe Hotel',
      kind: 'hotel',
      city: 'Brooklyn',
      region: 'New York',
      regionCode: 'NY',
      country: 'United States',
      countryCode: 'US',
      lat: 40.7219,
      lon: -73.9577,
      tz: 'America/New_York',
      address: '80 Wythe Ave, Brooklyn, NY 11249',
    },
    '2026-11-19T15:00',
    '2026-11-22T11:00',
    'WY88213',
  );
  addItem(t, 'itm_nyc_dinner', {
    type: 'restaurant',
    title: 'Dinner at Lilia',
    confirmation: 'R-5521',
    startAt: instantFromLocal('2026-11-20T19:30', 'America/New_York'),
    startTz: 'America/New_York',
    details: { type: 'restaurant', partySize: 2 },
  });
  addItem(t, 'itm_nyc_show', {
    type: 'activity',
    title: 'Hadestown',
    startAt: instantFromLocal('2026-11-21T19:00', 'America/New_York'),
    startTz: 'America/New_York',
    endAt: instantFromLocal('2026-11-21T21:30', 'America/New_York'),
    endTz: 'America/New_York',
    details: { type: 'activity', venue: 'Walter Kerr Theatre', tickets: 'Orchestra H 101, 102' },
  });
  flight(t, 'B6 1033', 'LGA', 'RSW', '2026-11-22T15:40', '2026-11-22T18:44', { seat: '2C' });
}

{
  const t = trip('trp_lis26');
  flight(
    t,
    'DL 2903',
    'RSW',
    'JFK',
    '2026-12-11T12:10',
    '2026-12-11T15:05',
    { seat: '9A' },
    'HN4XQ2',
  );
  flight(
    t,
    'TP 210',
    'JFK',
    'LIS',
    '2026-12-11T21:35',
    '2026-12-12T09:40',
    { seat: '4K', cabin: 'Business' },
    'HN4XQ2',
  );
  stay(
    t,
    {
      key: 'lis_bnb',
      name: 'Airbnb in Alfama',
      kind: 'airbnb',
      city: 'Lisbon',
      region: 'Lisbon',
      regionCode: null,
      country: 'Portugal',
      countryCode: 'PT',
      lat: 38.7114,
      lon: -9.1294,
      tz: 'Europe/Lisbon',
      address: 'Alfama, Lisbon',
    },
    '2026-12-12T13:00',
    '2026-12-19T11:00',
    'HMZ8K2QW',
  );
  flight(
    t,
    'TP 209',
    'LIS',
    'JFK',
    '2026-12-19T13:30',
    '2026-12-19T16:20',
    { seat: '4K', cabin: 'Business' },
    'HN4XQ2',
  );
  flight(
    t,
    'DL 2904',
    'JFK',
    'RSW',
    '2026-12-19T19:05',
    '2026-12-19T22:12',
    { seat: '9A' },
    'HN4XQ2',
  );
}

// ---------- Past ----------

{
  const t = trip('trp_chi26');
  flight(t, 'AA 2731', 'RSW', 'ORD', '2026-06-05T09:20', '2026-06-05T11:21');
  stay(
    t,
    {
      key: 'chi_hotel',
      name: 'Kimpton Gray Hotel',
      kind: 'hotel',
      brand: 'Kimpton',
      program: 'IHG One Rewards',
      city: 'Chicago',
      region: 'Illinois',
      regionCode: 'IL',
      country: 'United States',
      countryCode: 'US',
      lat: 41.8795,
      lon: -87.6323,
      tz: 'America/Chicago',
      address: '122 W Monroe St, Chicago, IL 60603',
    },
    '2026-06-05T15:00',
    '2026-06-08T11:00',
    '71882034',
  );
  flight(t, 'AA 2732', 'ORD', 'RSW', '2026-06-08T17:05', '2026-06-08T21:02');
}
{
  const t = trip('trp_den26');
  flight(t, 'UA 1689', 'RSW', 'DEN', '2026-04-16T07:00', '2026-04-16T09:05');
  stay(
    t,
    {
      key: 'den_hotel',
      name: 'Hyatt Regency Denver',
      kind: 'hotel',
      brand: 'Hyatt Regency',
      program: 'World of Hyatt',
      city: 'Denver',
      region: 'Colorado',
      regionCode: 'CO',
      country: 'United States',
      countryCode: 'US',
      lat: 39.7428,
      lon: -104.9913,
      tz: 'America/Denver',
      address: '650 15th St, Denver, CO 80202',
    },
    '2026-04-16T15:00',
    '2026-04-20T12:00',
    '55012944',
  );
  flight(t, 'UA 1690', 'DEN', 'RSW', '2026-04-20T13:10', '2026-04-20T18:52');
}
{
  const t = trip('trp_bos26');
  flight(t, 'B6 1132', 'RSW', 'BOS', '2026-02-12T06:45', '2026-02-12T10:01');
  stay(
    t,
    {
      key: 'bos_hotel',
      name: 'Omni Parker House',
      kind: 'hotel',
      brand: 'Omni',
      city: 'Boston',
      region: 'Massachusetts',
      regionCode: 'MA',
      country: 'United States',
      countryCode: 'US',
      lat: 42.3577,
      lon: -71.0602,
      tz: 'America/New_York',
      address: '60 School St, Boston, MA 02108',
    },
    '2026-02-12T15:00',
    '2026-02-15T12:00',
    '3310847',
  );
  flight(t, 'B6 1133', 'BOS', 'RSW', '2026-02-15T16:20', '2026-02-15T19:52');
}
{
  const t = trip('trp_aus25');
  flight(t, 'DL 1442', 'RSW', 'ATL', '2025-11-06T07:10', '2025-11-06T08:52');
  flight(t, 'DL 2367', 'ATL', 'AUS', '2025-11-06T10:05', '2025-11-06T11:38');
  stay(
    t,
    {
      key: 'aus_hotel',
      name: 'Hotel San José',
      kind: 'hotel',
      city: 'Austin',
      region: 'Texas',
      regionCode: 'TX',
      country: 'United States',
      countryCode: 'US',
      lat: 30.2512,
      lon: -97.7495,
      tz: 'America/Chicago',
      address: '1316 S Congress Ave, Austin, TX 78704',
    },
    '2025-11-06T15:00',
    '2025-11-09T12:00',
    'SJ-20871',
  );
  flight(t, 'DL 1914', 'AUS', 'ATL', '2025-11-09T13:30', '2025-11-09T16:50');
  flight(t, 'DL 1443', 'ATL', 'RSW', '2025-11-09T18:20', '2025-11-09T19:58');
}
{
  const t = trip('trp_sea25');
  flight(t, 'AS 1063', 'RSW', 'SEA', '2025-08-14T08:00', '2025-08-14T11:20');
  stay(
    t,
    {
      key: 'sea_bnb',
      name: 'Airbnb in Fremont',
      kind: 'airbnb',
      city: 'Seattle',
      region: 'Washington',
      regionCode: 'WA',
      country: 'United States',
      countryCode: 'US',
      lat: 47.6505,
      lon: -122.3493,
      tz: 'America/Los_Angeles',
      address: 'Fremont, Seattle, WA',
    },
    '2025-08-14T16:00',
    '2025-08-20T10:00',
    'HM2K9PXX',
  );
  flight(t, 'AS 1062', 'SEA', 'RSW', '2025-08-20T12:45', '2025-08-20T21:10');
}
{
  const t = trip('trp_par25');
  flight(t, 'DL 2903', 'RSW', 'ATL', '2025-05-09T12:30', '2025-05-09T14:12', {}, 'GQ8V1M');
  flight(
    t,
    'AF 689',
    'ATL',
    'CDG',
    '2025-05-09T17:20',
    '2025-05-10T07:45',
    { seat: '31A' },
    'GQ8V1M',
  );
  stay(
    t,
    {
      key: 'par_hotel',
      name: 'Hôtel des Grands Boulevards',
      kind: 'hotel',
      city: 'Paris',
      region: 'Ile-de-France',
      regionCode: null,
      country: 'France',
      countryCode: 'FR',
      lat: 48.8713,
      lon: 2.3474,
      tz: 'Europe/Paris',
      address: '17 Boulevard Poissonnière, 75002 Paris',
    },
    '2025-05-10T15:00',
    '2025-05-16T11:00',
    'GB-47120',
  );
  flight(
    t,
    'AF 688',
    'CDG',
    'ATL',
    '2025-05-16T10:25',
    '2025-05-16T14:35',
    { seat: '31A' },
    'GQ8V1M',
  );
  flight(t, 'DL 1443', 'ATL', 'RSW', '2025-05-16T18:20', '2025-05-16T19:58', {}, 'GQ8V1M');
}

{
  // A sample cruise, so playback can show the ship sailing port to port.
  const t = trip('trp_cru27');
  const port: Place = {
    ...base('plc_port_miami'),
    name: 'PortMiami',
    address: '1015 N America Way, Miami, FL',
    lat: 25.7781,
    lon: -80.1794,
    tz: 'America/New_York',
    city: 'Miami',
    region: 'Florida',
    regionCode: 'FL',
    country: 'United States',
    countryCode: 'US',
    iata: null,
    kind: 'port',
    geo: 'geocoder',
  };
  places.push(port);
  addItem(t, 'itm_cru27', {
    type: 'cruise',
    title: 'Western Caribbean cruise',
    provider: 'Royal Caribbean',
    confirmation: 'RC4471902',
    startAt: instantFromLocal('2027-02-13T16:00', 'America/New_York'),
    startTz: 'America/New_York',
    endAt: instantFromLocal('2027-02-20T07:00', 'America/New_York'),
    endTz: 'America/New_York',
    startPlaceId: port.id,
    endPlaceId: port.id,
    details: {
      type: 'cruise',
      line: 'Royal Caribbean',
      ship: 'Icon of the Seas',
      cabin: '9284',
      ports: [
        { name: 'Roatán', lat: 16.3298, lon: -86.5312, date: '2027-02-16' },
        { name: 'Costa Maya', lat: 18.7148, lon: -87.7084, date: '2027-02-17' },
        { name: 'Cozumel', lat: 20.5083, lon: -86.9458, date: '2027-02-18' },
      ],
    },
  });
}

const mplsAirports = new Map([
  ['plc_rsw', 'plc_air_RSW'],
  ['plc_msp', 'plc_air_MSP'],
]);

/** The Minneapolis trip, with its airports shared with the other sample trips. */
const mplsItems: Item[] = MPLS_ITEMS.map((i) => ({
  ...i,
  sourceId: SAMPLE_SOURCE,
  startPlaceId: (i.startPlaceId && mplsAirports.get(i.startPlaceId)) || i.startPlaceId,
  endPlaceId: (i.endPlaceId && mplsAirports.get(i.endPlaceId)) || i.endPlaceId,
  notes:
    i.id === 'itm_bnb'
      ? 'Door code 4417. Parking on the street after 6 PM.\n\n- [x] Message Dana about early check-in\n- [ ] Leave the keys on the counter'
      : i.notes,
}));

export const DEMO = {
  places: [...airportById.values(), ...MPLS_PLACES.filter((p) => p.kind !== 'airport'), ...places],
  trips: [
    {
      ...MPLS_TRIP,
      notes:
        'Work trip, with the weekend of the 10th free.\n\n- [x] Book the Airbnb\n- [ ] Pack a warm jacket',
    },
    ...trips,
  ],
  items: [...mplsItems, ...items],
  // Sample wallet, made up, so the Wallet screens can be reviewed before real data is added.
  programs: [
    {
      ...base('prg_s1'),
      programId: 'chase_ur',
      name: 'Chase Ultimate Rewards',
      balance: 142_300,
      tier: null,
      expiresOn: null,
      checkedOn: '2026-10-08',
      notes: '',
    },
    {
      ...base('prg_s2'),
      programId: 'hyatt',
      name: 'World of Hyatt',
      balance: 61_450,
      tier: 'Explorist',
      expiresOn: null,
      checkedOn: '2026-10-08',
      notes: '',
    },
    {
      ...base('prg_s3'),
      programId: 'marriott',
      name: 'Marriott Bonvoy',
      balance: 118_900,
      tier: 'Platinum',
      expiresOn: null,
      checkedOn: '2026-10-08',
      notes: '',
    },
    {
      ...base('prg_s4'),
      programId: 'delta',
      name: 'Delta SkyMiles',
      balance: 54_200,
      tier: 'Gold Medallion',
      expiresOn: null,
      checkedOn: '2026-10-08',
      notes: '',
    },
    {
      ...base('prg_s5'),
      programId: 'ihg',
      name: 'IHG One Rewards',
      balance: 32_000,
      tier: null,
      expiresOn: '2026-12-02',
      checkedOn: '2026-10-08',
      notes: '',
    },
  ] satisfies Program[],
  cards: [
    {
      ...base('crd_s1'),
      name: 'Sapphire Reserve',
      issuer: 'Chase',
      last4: '4021',
      annualFee: 795,
      feeDueOn: '2026-11-18',
      openedOn: '2022-11-18',
      earns: 'chase_ur',
      benefits: [
        {
          id: 'b1',
          title: 'The Edit hotel credit',
          value: 250,
          period: 'semiannual',
          resetsOn: '2026-12-31',
          used: 0,
          note: null,
        },
        {
          id: 'b2',
          title: 'Dining credit',
          value: 150,
          period: 'semiannual',
          resetsOn: '2026-12-31',
          used: 60,
          note: null,
        },
        {
          id: 'b3',
          title: 'Travel credit',
          value: 300,
          period: 'cardmember-year',
          resetsOn: '2026-11-18',
          used: 300,
          note: null,
        },
        {
          id: 'b4',
          title: 'Lounge access',
          value: null,
          period: 'ongoing',
          resetsOn: null,
          used: null,
          note: 'Priority Pass and Chase Sapphire Lounges',
        },
      ],
      notes: '',
    },
    {
      ...base('crd_s2'),
      name: 'World of Hyatt Card',
      issuer: 'Chase',
      last4: '7710',
      annualFee: 95,
      feeDueOn: '2027-02-04',
      openedOn: '2023-02-04',
      earns: 'hyatt',
      benefits: [
        {
          id: 'b1',
          title: 'Free night, category 1 to 4',
          value: null,
          period: 'cardmember-year',
          resetsOn: null,
          used: null,
          note: 'Each card anniversary',
        },
      ],
      notes: '',
    },
    {
      ...base('crd_s3'),
      name: 'Platinum Card',
      issuer: 'American Express',
      last4: '1009',
      annualFee: 895,
      feeDueOn: '2027-05-01',
      openedOn: '2021-05-01',
      earns: 'amex_mr',
      benefits: [
        {
          id: 'b1',
          title: 'Uber Cash',
          value: 15,
          period: 'monthly',
          resetsOn: '2026-10-31',
          used: 0,
          note: null,
        },
        {
          id: 'b2',
          title: 'Hotel credit',
          value: 300,
          period: 'semiannual',
          resetsOn: '2026-12-31',
          used: 0,
          note: 'Fine Hotels and The Hotel Collection',
        },
        {
          id: 'b3',
          title: 'Airline fee credit',
          value: 200,
          period: 'annual',
          resetsOn: '2026-12-31',
          used: 150,
          note: null,
        },
      ],
      notes: '',
    },
  ] satisfies Card[],
  perks: [
    {
      ...base('prk_s1'),
      kind: 'certificate',
      title: 'Marriott free night, up to 35k',
      programId: 'marriott',
      cardId: null,
      value: null,
      unit: null,
      progress: null,
      expiresOn: '2026-11-30',
      used: false,
      notes: '',
    },
    {
      ...base('prk_s2'),
      kind: 'bonus',
      title: 'Platinum spending bonus',
      programId: 'amex_mr',
      cardId: 'crd_s3',
      value: 80_000,
      unit: 'points',
      progress: { done: 4_200, target: 8_000 },
      expiresOn: '2026-11-15',
      used: false,
      notes: '',
    },
  ] satisfies Perk[],
  // Sample points briefs for the News tab (made up).
  briefs: [
    {
      id: 'brf_daily-2026-10-11',
      rev: 0,
      updatedAt: 0,
      createdAt: 0,
      deleted: false,
      kind: 'daily',
      date: '2026-10-11',
      label: 'Sun, Oct 11',
      title: '☀️ Points brief · Sun, Oct 11',
      headline: '2 to act on · 1 good to know',
      itemCount: 3,
      sections: [
        {
          name: 'Act now',
          items: [
            {
              emoji: '💰',
              title: 'Example Hotels points +100% bonus',
              cpp: '0.6¢/pt to buy',
              dates: 'Posted Oct 10 · ⏳ Ends Oct 20',
              minPoints: 1000,
              maxPoints: 160000,
              why: 'Under their usual 0.8¢ best sale, and you have a stay booked.',
              source: 'Example Blog',
              url: 'https://example.com/hotel-sale',
            },
            {
              emoji: '✈️',
              title: 'Example Air: 40% off award flights to Europe',
              cpp: '~2.1¢/pt value',
              dates: 'Posted Oct 11 · Book by Oct 25',
              why: 'Good fit for a spring trip.',
              source: 'Example News',
              url: 'https://example.com/award-sale',
            },
          ],
        },
        { name: '⏳ Ending soon', lines: ['💳 Example card offer, 80k bonus · ends Oct 14'] },
      ],
      markdown: '',
      writtenAt: null,
    },
    {
      id: 'brf_weekly-2026-10-11',
      rev: 0,
      updatedAt: 0,
      createdAt: 0,
      deleted: false,
      kind: 'weekly',
      date: '2026-10-11',
      label: 'Oct 5 to 11',
      title: '🗞️ Weekly points recap · Oct 5 to 11',
      headline: '3 worth knowing',
      itemCount: 1,
      sections: [
        {
          name: 'Biggest news',
          items: [
            {
              emoji: '📣',
              title: 'Example Air miles: 90% bonus when you buy',
              cpp: '1.4¢/pt to buy',
              dates: 'Ends Oct 29',
              source: 'Example Blog',
              url: 'https://example.com/aeroplan',
            },
          ],
        },
      ],
      markdown: '',
      writtenAt: null,
    },
  ] satisfies Brief[],
  redemptions: [
    {
      ...base('rdm_s1'),
      programId: 'hyatt',
      on: '2026-06-12',
      points: 25_000,
      cashValue: 640,
      fees: 0,
      title: 'Hyatt Regency Lisbon, 2 nights',
      tripId: null,
      notes: '',
    },
    {
      ...base('rdm_s2'),
      programId: 'delta',
      on: '2026-04-02',
      points: 18_000,
      cashValue: 236,
      fees: 5.6,
      title: 'RSW to MSP, one way',
      tripId: null,
      notes: '',
    },
  ] satisfies Redemption[],
  // Made-up balance history for the sample wallet, ending on its balances, for Over time.
  balanceSnapshots: (
    [
      ['prg_s1', '2025-11-02', 96_400, null],
      ['prg_s1', '2026-02-14', 118_900, '+22,500 card bonus'],
      ['prg_s1', '2026-05-20', 131_050, null],
      ['prg_s1', '2026-08-01', 139_800, null],
      ['prg_s1', '2026-10-08', 142_300, null],
      ['prg_s2', '2025-12-01', 72_100, null],
      ['prg_s2', '2026-03-10', 86_450, null],
      ['prg_s2', '2026-06-12', 61_450, 'after 25,000 Hyatt Regency Lisbon'],
      ['prg_s3', '2026-01-05', 88_200, null],
      ['prg_s3', '2026-07-22', 112_400, '+24,200 from stays'],
      ['prg_s3', '2026-10-08', 118_900, null],
      ['prg_s4', '2025-11-20', 61_800, null],
      ['prg_s4', '2026-04-02', 43_800, 'after 18,000 RSW to MSP'],
      ['prg_s4', '2026-09-15', 54_200, null],
      ['prg_s5', '2026-02-01', 32_000, null],
    ] as const
  ).map(([entryId, on, balance, note], i) => ({
    ...base(`bal_s${i}`),
    entryId,
    on,
    at: Date.parse(`${on}T12:00:00Z`),
    balance,
    source: 'backfill' as const,
    note,
  })) satisfies BalanceSnapshot[],
  // A made-up delay on the flight home, so the delay screens can be reviewed.
  flightLive: [
    {
      ...base('itm_dl_back'),
      itemId: 'itm_dl_back',
      faFlightId: null,
      status: 'Delayed',
      cancelled: false,
      diverted: false,
      scheduledOut: instantFromLocal('2026-10-16T17:10', 'America/Chicago'),
      scheduledOff: null,
      scheduledOn: null,
      scheduledIn: instantFromLocal('2026-10-16T21:15', 'America/New_York'),
      estimatedOut: instantFromLocal('2026-10-16T17:55', 'America/Chicago'),
      estimatedOff: null,
      estimatedOn: null,
      estimatedIn: instantFromLocal('2026-10-16T21:58', 'America/New_York'),
      actualOut: null,
      actualOff: null,
      actualOn: null,
      actualIn: null,
      gateOrigin: 'G14',
      gateOriginWas: 'G7',
      gateDestination: null,
      terminalOrigin: '1',
      terminalDestination: null,
      baggageClaim: null,
      aircraftType: 'A321',
      registration: null,
      inboundFaFlightId: null,
      inbound: {
        origin: 'ATL',
        estimatedIn: instantFromLocal('2026-10-16T17:20', 'America/Chicago'),
        delayMinutes: 40,
        status: 'En Route',
        ident: 'DL1840',
        departAt: instantFromLocal('2026-10-16T15:05', 'America/New_York'),
        departed: true,
        landed: false,
        atGate: false,
        originLat: 33.64,
        originLon: -84.43,
        position: null,
      },
      route: null,
      routeSource: null,
      routeDistanceMi: null,
      lastPolledAt: Date.parse('2026-10-11T12:00:00Z'),
      nextPollAt: null,
    },
  ] satisfies FlightLive[],
};
